import { runCordovaPluginCall } from "@helpers/cordovaBridge";

type FirebaseAnalyticsValue = string | number | boolean;

interface IFirebaseAnalyticsPlugin {
  getId: (
    success: (id: string | null) => void,
    error?: (error: string) => void
  ) => void;
  getAnalyticsAppInstanceId?: (
    success: (id: string | null) => void,
    error?: (error: string) => void
  ) => void;
  logEvent: (
    eventName: string,
    eventParams: Record<string, FirebaseAnalyticsValue>,
    success?: () => void,
    error?: (error: string) => void
  ) => void;
  setUserId: (
    userId: string,
    success?: (result?: any) => void,
    error?: (error: any) => void
  ) => void;
}

type FirebaseAnalyticsParams = Record<string, FirebaseAnalyticsValue | null | undefined>;

declare const window: any;

let firebaseAnalyticsPluginPromise: Promise<IFirebaseAnalyticsPlugin | undefined> | undefined;

function waitForFirebaseAnalyticsPlugin(): Promise<IFirebaseAnalyticsPlugin | undefined> {
  if (typeof window === "undefined" || !window.cordova) {
    return Promise.resolve(undefined);
  }

  const existingPlugin = (globalThis as any).FirebasePlugin;
  if (existingPlugin) {
    return Promise.resolve(existingPlugin);
  }

  if (!firebaseAnalyticsPluginPromise) {
    firebaseAnalyticsPluginPromise = new Promise((resolve) => {
      if (typeof document === "undefined") {
        resolve(undefined);
        return;
      }

      document.addEventListener("deviceready", () => {
        resolve((globalThis as any).FirebasePlugin);
      }, false);
    });
  }

  return firebaseAnalyticsPluginPromise;
}

function compactParams(params: FirebaseAnalyticsParams = {}): Record<string, FirebaseAnalyticsValue> {
  const compacted: Record<string, FirebaseAnalyticsValue> = {};

  Object.keys(params).forEach((key) => {
    const value = params[key];

    if (value === undefined || value === null) {
      return;
    }

    compacted[key] = value;
  });

  return compacted;
}

export async function setFirebaseAnalyticsUserId(userId?: string | number | null): Promise<boolean> {
  const plugin = await waitForFirebaseAnalyticsPlugin();

  if (!plugin) {
    return false;
  }

  const resolvedUserId = userId === undefined || userId === null ? "" : `${userId}`;

  return new Promise((resolve) => {
    const didStart = runCordovaPluginCall("FirebasePlugin.setUserId", () => {
      plugin.setUserId(
        resolvedUserId,
        () => resolve(true),
        (error: any) => {
          console.error("Error setting Firebase analytics userId", error);
          resolve(false);
        }
      );
      return true;
    }, false);

    if (!didStart) {
      resolve(false);
    }
  });
}

export function clearFirebaseAnalyticsUserId(): Promise<boolean> {
  return setFirebaseAnalyticsUserId("");
}

export async function getFirebaseAppInstanceId(): Promise<string | undefined> {
  const plugin = await waitForFirebaseAnalyticsPlugin();

  if (!plugin) {
    return undefined;
  }

  const getAnalyticsAppInstanceId = plugin.getAnalyticsAppInstanceId;

  if (!getAnalyticsAppInstanceId) {
    console.error("Firebase Analytics app_instance_id API is not available");
    return undefined;
  }

  return new Promise((resolve) => {
    const didStart = runCordovaPluginCall("FirebasePlugin.getAnalyticsAppInstanceId", () => {
      getAnalyticsAppInstanceId(
        (id: string | null) => resolve(id || undefined),
        (error: string) => {
          console.error("Error getting Firebase Analytics app_instance_id", error);
          resolve(undefined);
        }
      );
      return true;
    }, false);

    if (!didStart) {
      resolve(undefined);
    }
  });
}

export async function logFirebaseAnalyticsEvent(
  eventName: string,
  eventParams: FirebaseAnalyticsParams = {},
  userId?: string | number | null
): Promise<boolean> {

  const plugin = await waitForFirebaseAnalyticsPlugin();

  if (!plugin) {
    return false;
  }

  if (userId !== undefined && userId !== null && `${userId}`.length > 0) {
    await setFirebaseAnalyticsUserId(userId);
  }

  return new Promise((resolve) => {
    const didStart = runCordovaPluginCall(`FirebasePlugin.logEvent:${eventName}`, () => {
      plugin.logEvent(
        eventName,
        compactParams(eventParams),
        () => resolve(true),
        (error: string) => {
          console.error(`Error logging Firebase analytics event "${eventName}"`, error);
          resolve(false);
        }
      );
      return true;
    }, false);

    if (!didStart) {
      resolve(false);
    }
  });
}



// WEBPACK FOOTER //
// ./src/services/firebase/firebaseAnalytics.ts